import { Suspense, useLayoutEffect } from 'react';

import { theme } from '@braskai/ui-kit';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';

import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Amplify } from 'aws-amplify';

import { SomethingWrongError } from '@/widgets/error';

import { addRequestInterceptor } from '@/shared/api/interceptors';
import { getAuthConfig, queryClient } from '@/shared/config';
import { AMPLIFY_PROVIDER_STORAGE_KEY } from '@/shared/constants/amplify';
import { EmailConsentProvider } from '@/shared/contexts/EmailConsentContext';
import { store } from '@/shared/store-tmp';
import { initAdsTracking } from '@/shared/thirdParty/ads';
import { pushAttributionToDataLayer } from '@/shared/thirdParty/gtm';
import { initMixpanel } from '@/shared/thirdParty/mixpanel';
import { useInitReferral } from '@/shared/thirdParty/rewardful';
import { SentryRask } from '@/shared/thirdParty/sentry';
import { Loader } from '@/shared/ui/Loader';

import { IS_REACT_QUERY_TOOLS } from './config';
import { MaintenanceContainer } from './providers/MaintenanceContainer';
import { Notifications } from './providers/Notifications';
import { router } from './router';
import { ServerEnvSelector } from './ui';

initAdsTracking();
initMixpanel();
pushAttributionToDataLayer();

const provider = localStorage.getItem(AMPLIFY_PROVIDER_STORAGE_KEY) || '';
Amplify.configure(getAuthConfig(provider));

SentryRask.initIfAvailable();

// Enable 3d party services for local build
// sessionStorage.setItem('enableMixpanel', '1');
// sessionStorage.setItem('enableLogRocket', '1')

const AppComponent = () => {
  useLayoutEffect(() => {
    addRequestInterceptor();
  }, []);
  useInitReferral();

  return (
    <Suspense fallback={<Loader />}>
      <CssBaseline />
      <RouterProvider router={router} />
      {IS_REACT_QUERY_TOOLS && <ReactQueryDevtools />}
      <ServerEnvSelector />
    </Suspense>
  );
};

const App = () => (
  <Sentry.ErrorBoundary
    fallback={
      <SomethingWrongError
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          minWidth: '100%',
        }}
      />
    }
    showDialog={false}
  >
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <Notifications>
          <Provider store={store}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <QueryClientProvider client={queryClient}>
                <EmailConsentProvider>
                  <MaintenanceContainer>
                    <AppComponent />
                  </MaintenanceContainer>
                </EmailConsentProvider>
              </QueryClientProvider>
            </LocalizationProvider>
          </Provider>
        </Notifications>
      </ThemeProvider>
    </HelmetProvider>
  </Sentry.ErrorBoundary>
);

export default App;
